import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useStore } from 'react-redux'
import { useLocation } from 'react-router-dom'

import Header from '@/components-latest/layout/Header'
import Button from '@/components-latest/primitives/Button'
import Text from '@/components-latest/primitives/Text'
import CommonModal from '@/components-latest/shared/Modals/CommonModal'
import { DEAL_TYPE } from '@/constants/storeEvents'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'
import {
  useCreateBuyXGetYMutation,
  useEditBuyXGetYMutation
} from '@/ducks/offers/api/offers.api'
import {
  useOfferFormNavigateBack,
  useOfferFormSuccessNavigate
} from '@/hooks/useOfferFormNavigateBack/useOfferFormNavigateBack'
import { stashPostCreateCampaignsBaseline } from '@/ducks/campaigns/campaigns.cache.helpers'
import type { RootState } from '@/ducks/root.reducer'
import {
  changedBuyXGetYFields,
  trackDealsCreated,
  trackDealUpdated
} from '@/utils/dealEvents.utils'

import Styles from '../../OrdersPage/PaymentSummary/PaymentSummary.module.scss'
import FormDetails, { FormDetailsRef } from './FormDetails'

type Props = {
  onSuccess?: () => void
  name?: string
}

type LocationState = {
  offerId?: string
  expiryDate?: string
  expirationTime?: string
  itemsPurchased?: string | number
  itemsOffer?: string | number
  isCumulative?: boolean
  item1?: { id: string; name: string }
  item2?: { id: string; name: string; discountPercent: number }
  status?: string
}

const resolveLocationState = (
  locationState: unknown
): LocationState | undefined => {
  if (!locationState || typeof locationState !== 'object') return undefined

  if (
    'state' in locationState &&
    locationState.state &&
    typeof locationState.state === 'object'
  ) {
    return locationState.state as LocationState
  }

  return locationState as LocationState
}

export enum Mode {
  Create = 'create',
  Edit = 'edit'
}

const BuyXGetYForm = ({ name }: Props) => {
  const { t } = useTranslation()
  const handleGoBack = useOfferFormNavigateBack('buyX_getY')
  const navigateOnSuccess = useOfferFormSuccessNavigate('buyX_getY')
  const dispatch = useDispatch()
  const store = useStore()
  const location = useLocation()
  const navigationState = resolveLocationState(location.state)
  const {
    offerId,
    expiryDate,
    expirationTime,
    itemsPurchased,
    itemsOffer,
    isCumulative,
    item1,
    item2,
    status
  } = navigationState || {}

  const mode = offerId ? Mode.Edit : Mode.Create

  const formDetailsRef = useRef<FormDetailsRef>(null)
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [formDetails, setFormDetails] = useState({
    xItem: '',
    xItemName: '',
    xQuantity: '',
    yItem: '',
    yItemName: '',
    yQuantity: '',
    discount: '',
    expiration: '',
    isCumulative: false
  })

  const [createBuyXGetY, { isLoading }] = useCreateBuyXGetYMutation()
  const [editBuyXGetY] = useEditBuyXGetYMutation()

  const checkFormValidity = () => {
    const isValid = formDetailsRef.current?.isFormValid() || false
    setIsSubmitButtonDisabled(!isValid)
  }
  const defaultValues =
    mode === Mode.Edit
      ? {
          selectedMenuItem: [item1?.id || ''],
          selectedItemName: item1?.name || '',
          selectedMenuItemY: [item2?.id || ''],
          selectedItemYName: item2?.name || '',
          xItemQuantity: String(itemsPurchased ?? '1'),
          yItemQuantity: String(itemsOffer ?? '1'),
          discount: item2?.discountPercent || 50,
          expirationValue: expirationTime || '3h',
          isCumulative: isCumulative || false
        }
      : undefined

  useEffect(() => {
    checkFormValidity()
  }, [])

  const onOfferSubmit = () => {
    if (isSubmitButtonDisabled) return
    setFormDetails({
      xItem: formDetailsRef.current?.selectedMenuItem[0] || '',
      xItemName: formDetailsRef.current?.selectedItemName || '',
      xQuantity: formDetailsRef.current?.xItemQuantity || '',
      yItem: formDetailsRef.current?.selectedMenuItemY[0] || '',
      yItemName: formDetailsRef.current?.selectedItemYName || '',
      yQuantity: formDetailsRef.current?.yItemQuantity || '',
      discount: formDetailsRef.current?.discount || '',
      expiration: formDetailsRef.current?.expirationValue || '',
      isCumulative: formDetailsRef.current?.isCumulative || false
    })
    setIsModalOpen(true)
  }

  const handleConfirm = () => {
    const payload = {
      itemXId: formDetails.xItem,
      nbrItemsPurchased: parseInt(formDetails.xQuantity, 10),
      itemYId: formDetails.yItem,
      nbrItemsOffer: parseInt(formDetails.yQuantity, 10),
      discountPercentage: parseInt(formDetails.discount.replace('%', ''), 10),
      expirationTime: formDetails.expiration,
      cumulatif: formDetails.isCumulative
    }

    stashPostCreateCampaignsBaseline(store.getState as () => RootState)
    createBuyXGetY(payload)
      .unwrap()
      .then(response => {
        // One event per deal the API reports; nothing if it sends none.
        trackDealsCreated(response?.deals)
        dispatch(
          addSystemNotification({
            message: t('offers.offers-creation.success'),
            intent: 'success'
          })
        )
        setIsModalOpen(false)
        navigateOnSuccess(Mode.Create)
      })
      .catch(error => {
        dispatch(
          addSystemNotification({
            message: error?.message || t('offers.offers-creation.error'),
            intent: 'error'
          })
        )
      })
  }
  const handleEdit = () => {
    const discountData = {
      offerId: offerId || '',
      offer: {
        itemXId: formDetails.xItem,
        nbrItemsPurchased: parseInt(formDetails.xQuantity, 10),
        itemYId: formDetails.yItem,
        nbrItemsOffer: parseInt(formDetails.yQuantity, 10),
        discountPercentage: parseInt(formDetails.discount.replace('%', ''), 10),
        expirationTime: formDetails.expiration,
        cumulatif: formDetails.isCumulative
      }
    }

    editBuyXGetY(discountData)
      .unwrap()
      .then(() => {
        // Reported once the API confirmed the save; nothing if no field changed.
        trackDealUpdated(
          offerId,
          DEAL_TYPE.BUY_X_GET_Y,
          changedBuyXGetYFields(
            {
              itemXId: defaultValues?.selectedMenuItem?.[0],
              itemYId: defaultValues?.selectedMenuItemY?.[0],
              nbrItemsPurchased: defaultValues?.xItemQuantity,
              nbrItemsOffer: defaultValues?.yItemQuantity,
              discountPercentage: defaultValues?.discount,
              expirationTime: defaultValues?.expirationValue,
              cumulatif: defaultValues?.isCumulative
            },
            discountData.offer
          )
        )
        dispatch(
          addSystemNotification({
            message: t('offers.offers-edition.success'),
            intent: 'success'
          })
        )
        setIsModalOpen(false)
        navigateOnSuccess(Mode.Edit)
      })
      .catch(error => {
        dispatch(
          addSystemNotification({
            message: error?.message || t('offers.offers-edition.error'),
            intent: 'error'
          })
        )
      })
  }

  return (
    <>
      <Header
        title={
          mode === Mode.Create
            ? t('offers.create-offer-title')
            : t('offers.edit-offer-title', { name })
        }
        onGoBack={handleGoBack}>
        <Button
          disabled={isSubmitButtonDisabled || isLoading}
          loading={isLoading}
          onClick={onOfferSubmit}
          shape="rounded"
          variant={isSubmitButtonDisabled ? 'secondary' : 'branded'}>
          {t('offers.offers-creation.save')}
        </Button>
      </Header>

      <div>
        <FormDetails
          ref={formDetailsRef}
          onFormChange={checkFormValidity}
          defaultValues={defaultValues}
        />
      </div>

      <CommonModal
        open={isModalOpen}
        title={t('offers.buyX-getY.confirm-modal.title')}
        onOpenChange={() => setIsModalOpen(!isModalOpen)}
        body={
          <div className={Styles.details}>
            <Text variant="nextSubtitle" className={Styles.detailsTitle}>
              {t('offers.buyX-getY.confirm-modal.details')}
            </Text>
            <ul className={Styles.detailsList}>
              <li>
                {t('offers.buyX-getY.confirm-modal.buy')}{' '}
                {formDetails.xQuantity} {formDetails.xItemName}
              </li>
              <li>
                {t('offers.buyX-getY.confirm-modal.get')}{' '}
                {formDetails.yQuantity} {formDetails.yItemName}{' '}
                {t('offers.buyX-getY.confirm-modal.at')} {formDetails.discount}
                {t('offers.offers-details.percents-off')}
              </li>
              {formDetails.isCumulative && (
                <li>{t('offers.buyX-getY.cumulative')}</li>
              )}
              <li>
                {t('offers.buyX-getY.confirm-modal.ends-in')}{' '}
                {formDetails.expiration.replace('h', ' hours')}
              </li>
            </ul>
            <Text variant="nextCaption">
              *
              {t(
                'offers.select-offer-type.discount-costs-billed-to-restaurant'
              )}
            </Text>
          </div>
        }
        footer={
          <div className={Styles.footer}>
            <Button
              size="medium"
              variant="tertiary"
              type="button"
              onClick={() => setIsModalOpen(false)}>
              {t('common.modal.cancel')}
            </Button>
            <Button
              size="medium"
              variant="primary"
              type="button"
              onClick={mode === Mode.Create ? handleConfirm : handleEdit}>
              {t('common.modal.confirm')}
            </Button>
          </div>
        }
      />
    </>
  )
}

export default BuyXGetYForm
